import React from 'react';

import luxonGenerateConfig from '@rc-component/picker/lib/generate/luxon';
import { DatePicker as AntDatePicker } from 'antd-v5';
import type {
  RangePickerProps as AntRangePickerProps,
  PickerProps,
  PickerPropsWithMultiple,
} from 'antd-v5/lib/date-picker/generatePicker/interface';
import cn from 'classnames';
import type { DateTime } from 'luxon';
import type { PickerRef } from 'rc-picker';

import styles from './DatePicker.module.scss';

const customLuxonConfig = {
  ...luxonGenerateConfig,
  locale: {
    ...luxonGenerateConfig.locale,
    getWeekFirstDay() {
      return 0;
    },
  },
};

const LuxonDatePicker =
  AntDatePicker.generatePicker<DateTime>(customLuxonConfig);

export type DatePickerProps = Omit<
  PickerPropsWithMultiple<DateTime, PickerProps<DateTime>>,
  'prefix' | 'ref'
>;

export interface RangePickerProps
  extends Omit<AntRangePickerProps<DateTime>, 'prefix'> {
  label?: string;
}

const formatHint = (format: DatePickerProps['format']) =>
  typeof format === 'string' ? format.toUpperCase() : undefined;

const FloatingLabel: React.FC<{ children: React.ReactNode }> = props => (
  <span className={styles.floatingLabel} aria-hidden>
    {props.children}
  </span>
);

const DatePickerBase = React.forwardRef<PickerRef, DatePickerProps>(
  function DatePicker(props, ref) {
    const { className, placeholder, ...rest } = props;

    if (!placeholder) {
      return <LuxonDatePicker ref={ref} className={className} {...rest} />;
    }

    return (
      <LuxonDatePicker
        ref={ref}
        {...rest}
        className={cn(className, styles.picker)}
        prefix={<FloatingLabel>{placeholder}</FloatingLabel>}
        placeholder={formatHint(rest.format)}
      />
    );
  },
);

const RangePicker = React.forwardRef<PickerRef, RangePickerProps>(
  function RangePicker(props, ref) {
    const { className, label, ...rest } = props;

    if (!label) {
      return (
        <LuxonDatePicker.RangePicker
          ref={ref}
          className={className}
          {...rest}
        />
      );
    }

    const hint = formatHint(rest.format) ?? '';

    return (
      <LuxonDatePicker.RangePicker
        ref={ref}
        placeholder={[hint, hint]}
        {...rest}
        className={cn(className, styles.picker)}
        prefix={<FloatingLabel>{label}</FloatingLabel>}
      />
    );
  },
);

const DatePicker = Object.assign(DatePickerBase, {
  RangePicker,
  TimePicker: LuxonDatePicker.TimePicker,
  WeekPicker: LuxonDatePicker.WeekPicker,
  MonthPicker: LuxonDatePicker.MonthPicker,
  YearPicker: LuxonDatePicker.YearPicker,
  QuarterPicker: LuxonDatePicker.QuarterPicker,
});

export default DatePicker;
